import { useState, useEffect, useCallback } from 'react';
import { auth, storage, uploadBytesWithTimeout, getStorageErrorMessage, createAuditLog } from '../../../lib/firebase';
import { MASTER_STAGES } from '../../MasterPipeline';
import { ref } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { Application, AuditLog, Batch, BatchFolder, BatchLog, ServiceProvider } from '../../../types';
import { safeFetch, fetchWithRetry } from '../../../lib/api';
import { workspaceHeader } from '../../../lib/workspaces';
import { notifyToast } from '../../../lib/toastStore';
import { confirmDialog } from '../../../lib/confirmStore';

const compressImage = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) return file;
  try {
    return await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: file.type,
    });
  } catch (err) {
    console.warn('Image compression failed, using original:', err);
    return file;
  }
};

export function useBatches(options: {
  adminEmail: string;
  applications: Application[];
  setApplications: (apps: Application[]) => void;
}) {
  const { adminEmail, applications, setApplications } = options;

  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchFolders, setBatchFolders] = useState<BatchFolder[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [isCreatingBatch, setIsCreatingBatch] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchName, setBatchName] = useState('');
  const [batchDescription, setBatchDescription] = useState('');
  const [batchProgramTypes, setBatchProgramTypes] = useState<string[]>([]);
  const [batchSearchQuery, setBatchSearchQuery] = useState('');
  const [batchSelectedApplicantIds, setBatchSelectedApplicantIds] = useState<string[]>([]);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailContent, setBulkEmailContent] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastAttachments, setBroadcastAttachments] = useState<{ name: string; base64Content: string; mimeType: string }[]>([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastProgress, setBroadcastProgress] = useState<{ completed: number; total: number; totalRecipients: number } | null>(null);
  const [broadcastSummary, setBroadcastSummary] = useState<{ success: boolean; totalSent: number; totalChunks: number; failedChunks: number; failedRecipients: string[] } | null>(null);
  const [broadcastSenderAddress, setBroadcastSenderAddress] = useState('');
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('');
  const [cohortSearch, setCohortSearch] = useState('');
  const [cohortStatusFilter, setCohortStatusFilter] = useState('All');
  const [cohortProgramFilter, setCohortProgramFilter] = useState('All');

  // Batch logs
  const [batchLogs, setBatchLogs] = useState<BatchLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [newBatchLogText, setNewBatchLogText] = useState('');
  const [batchLogFile, setBatchLogFile] = useState<File | null>(null);
  const [isUploadingBatchLogFile, setIsUploadingBatchLogFile] = useState(false);
  const [batchLogFileUrl, setBatchLogFileUrl] = useState<string | null>(null);
  const [batchLogFileName, setBatchLogFileName] = useState<string | null>(null);
  const [batchLogFileProgress, setBatchLogFileProgress] = useState<number | null>(null);

  // Batch applicant search
  const [isAddApplicantOpen, setIsAddApplicantOpen] = useState(false);
  const [addApplicantSearch, setAddApplicantSearch] = useState('');

  // Batch notes
  const [batchNotes, setBatchNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // PT7-001: Now using shared createAuditLog from firebase.ts (server-side write)

  const fetchBatchLogs = async () => {
    if (!selectedBatchId) {
      setBatchLogs([]);
      return;
    }
    setIsLoadingLogs(true);
    try {
      const data = await safeFetch(`/api/admin/batches/${selectedBatchId}/logs`);
      setBatchLogs((data.logs || []) as BatchLog[]);
    } catch (err) {
      console.error("Error loading batch logs:", err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const fetchBatchFolders = async () => {
    try {
      const data = await safeFetch('/api/admin/batch-folders');
      setBatchFolders((data.folders || []) as BatchFolder[]);
    } catch (err) {
      console.warn("Could not fetch batch folders:", err);
    }
  };

  const fetchBatches = async () => {
    try {
      const data = await safeFetch('/api/admin/batches');
      setBatches((data.batches || []) as Batch[]);
    } catch (err) {
      console.warn("Could not fetch batches:", err);
    }
  };

  const handleCreateFolder = async (folderData: { name: string; description?: string; tags?: string[]; parentId?: string | null }) => {
    try {
      const res = await safeFetch('/api/admin/batch-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderData),
      });
      await fetchBatchFolders();
      showToast('Folder created', 'success');
      return res.folder as BatchFolder;
    } catch (err: any) {
      console.error("Failed to create folder:", err);
      showToast(err.message || 'Failed to create folder', 'error');
      throw err;
    }
  };

  const handleUpdateFolder = async (folderId: string, updates: { name?: string; description?: string; tags?: string[]; parentId?: string | null }) => {
    try {
      await safeFetch(`/api/admin/batch-folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      await fetchBatchFolders();
      showToast('Folder updated', 'success');
    } catch (err: any) {
      console.error("Failed to update folder:", err);
      showToast(err.message || 'Failed to update folder', 'error');
      throw err;
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    const folder = batchFolders.find(f => f.folderId === folderId);
    if (!folder) return;
    if (!(await confirmDialog(`Are you sure you want to delete folder "${folder.name}"? Subfolders and batches inside will be moved to its parent level.`))) return;
    try {
      await safeFetch(`/api/admin/batch-folders/${folderId}`, { method: 'DELETE' });
      await fetchBatchFolders();
      await fetchBatches();
      if (currentFolderId === folderId) {
        setCurrentFolderId(folder.parentId || null);
      }
      showToast('Folder deleted', 'success');
    } catch (err: any) {
      console.error("Failed to delete folder:", err);
      showToast(err.message || 'Failed to delete folder', 'error');
    }
  };

  const handleCreateBatch = async (e?: React.FormEvent, targetFolderId?: string | null) => {
    e?.preventDefault();
    if (!batchName.trim()) {
      notifyToast('warning', 'Please enter a batch name.');
      return;
    }
    try {
      const data = await safeFetch('/api/admin/batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: batchName.trim(),
          description: batchDescription.trim(),
          programTypes: batchProgramTypes,
          tags: [],
          folderId: targetFolderId !== undefined ? targetFolderId : currentFolderId,
        }),
      });
      await createAuditLog('CREATE_BATCH', data.batch.batchId, `Created batch: ${batchName.trim()}`);
      await fetchBatches();
      setIsCreatingBatch(false);
      setBatchName('');
      setBatchDescription('');
      showToast('Batch created', 'success');
    } catch (err) {
      console.error("Failed to create batch:", err);
      showToast('Failed to create batch', 'error');
    }
  };

  const handleDeleteBatch = async (batchId: string) => {
    const batch = batches.find(b => b.batchId === batchId);
    if (!batch) return;
    if (!(await confirmDialog(`Are you sure you want to delete batch "${batch.name}"? This cannot be undone.`))) return;
    try {
      await safeFetch(`/api/admin/batches/${batchId}`, { method: 'DELETE' });
      setBatches(prev => prev.filter(b => b.batchId !== batchId));
      if (selectedBatchId === batchId) {
        setSelectedBatchId(null);
      }
      await createAuditLog('DELETE_BATCH', batchId, `Deleted batch: ${batch.name}`);
      showToast('Batch deleted', 'success');
    } catch (err) {
      console.error("Failed to delete batch:", err);
      showToast('Failed to delete batch', 'error');
    }
  };

  const handleUpdateBatchDetail = async (batchId: string, updates: { name?: string; description?: string; programTypes?: string[]; status?: string; folderId?: string | null }) => {
    try {
      const payload: Record<string, any> = { ...updates };
      if (updates.programTypes) {
        payload.programType = updates.programTypes[0] || '';
      }
      await safeFetch(`/api/admin/batches/${batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setBatches(prev => prev.map(b => b.batchId === batchId ? { ...b, ...payload, updatedAt: new Date().toISOString() } : b));
      await createAuditLog('UPDATE_BATCH', batchId, `Updated batch details: ${JSON.stringify(updates)}`);
      showToast('Batch updated', 'success');
    } catch (err) {
      console.error("Failed to update batch:", err);
      showToast('Failed to update batch', 'error');
    }
  };

  const handleUpdateBatchTags = async (batchId: string, tags: string[]) => {
    try {
      await safeFetch(`/api/admin/batches/${batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags, updatedAt: new Date().toISOString() }),
      });
      setBatches(prev => prev.map(b => b.batchId === batchId ? { ...b, tags } : b));
    } catch (err) {
      console.error("Failed to update batch tags:", err);
    }
  };

  // Resolves true when the update was written, false when it failed.
  // The failure is still only logged here (long-standing behavior the
  // single-member flows rely on), but callers that report an outcome
  // to the admin — the bulk relocate/remove handlers — check the
  // result and never claim or audit a success that did not land.
  const handleUpdateBatchMembers = async (batchId: string, newMemberIds: string[], changeDetail?: string): Promise<boolean> => {
    try {
      await safeFetch(`/api/admin/batches/${batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicantIds: newMemberIds, updatedAt: new Date().toISOString() }),
      });
      setBatches(prev => prev.map(b => b.batchId === batchId ? { ...b, applicantIds: newMemberIds, totalApplicants: newMemberIds.length } : b));
      const logDetail = changeDetail || `Updated cohort members (${newMemberIds.length} total)`;
      await createAuditLog('UPDATE_BATCH_MEMBERS', batchId, `${logDetail} [by ${adminEmail} at ${new Date().toISOString()}]`);
      return true;
    } catch (err) {
      console.error("Failed to update batch members:", err);
      return false;
    }
  };

  const handleSaveBatchNotes = async () => {
    if (!selectedBatchId) return;
    setIsSavingNotes(true);
    try {
      await safeFetch(`/api/admin/batches/${selectedBatchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: batchNotes, updatedAt: new Date().toISOString() }),
      });
      showToast('Batch notes saved', 'success');
    } catch (err) {
      console.error("Failed to save batch notes:", err);
      showToast('Failed to save notes', 'error');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!selectedBatchId || !bulkStatusValue) {
      if (!bulkStatusValue) { notifyToast('warning', 'Please select a stage to apply.'); }
      return;
    }
    const batch = batches.find(b => b.batchId === selectedBatchId);
    if (!batch || !batch.applicantIds.length) {
      notifyToast('warning', 'No candidates in this batch to update.');
      return;
    }
    const stageIndex = Number(bulkStatusValue);
    const stageLabel = MASTER_STAGES[stageIndex]?.label || bulkStatusValue;
    if (!(await confirmDialog(`Are you sure you want to update the pipeline stage of all ${batch.applicantIds.length} candidates in this batch to "${stageLabel}"?`))) return;
    try {
      const now = new Date().toISOString();
      const status = MASTER_STAGES[stageIndex]?.id || bulkStatusValue;
      // Use server API to bypass Firestore permission issues
      await safeFetch('/api/admin/applications/bulk-master-stage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicationIds: batch.applicantIds,
          masterStage: stageIndex,
          status,
        }),
      });

      setApplications(applications.map(app => batch.applicantIds.includes(app.applicationId) ? { ...app, masterStage: stageIndex, status: status as any } : app));
      await createAuditLog('BULK_STATUS_UPDATE', selectedBatchId, `Updated ${batch.applicantIds.length} candidates to pipeline stage: ${stageLabel}`);
      showToast(`Updated ${batch.applicantIds.length} candidates to "${stageLabel}"`, 'success');
    } catch (err: any) {
      console.error("Bulk stage update failed:", err);
      showToast(err.message || 'Bulk stage update failed', 'error');
    }
  };

  const handleSendBulkEmail = async () => {
    if (!selectedBatchId || !bulkEmailSubject.trim() || !bulkEmailContent.trim()) {
      notifyToast('warning', 'Please fill in subject and content.');
      return;
    }
    notifyToast('info', `Bulk email feature: would send "${bulkEmailSubject}" to all cohort candidates (simulated). In production, this would trigger a Cloud Function to send emails via SendGrid/Mailgun.`);
    await createAuditLog('SEND_BULK_EMAIL', selectedBatchId, `Sent bulk email: ${bulkEmailSubject}`);
  };

  const handleBroadcastEmail = async () => {
    if (!selectedBatchId || !broadcastSubject.trim() || !broadcastContent.trim()) {
      notifyToast('warning', 'Please fill in subject and content.');
      return;
    }

    const batch = batches.find(b => b.batchId === selectedBatchId);
    if (!batch) { notifyToast('warning', 'Batch not found.'); return; }

    const members = applications.filter(app => batch.applicantIds.includes(app.applicationId));
    const recipients = members
      .map(app => ({ email: (app as any).email || app.userEmail || '', applicationId: app.applicationId, applicantData: app }))
      .filter(r => r.email);

    if (recipients.length === 0) {
      notifyToast('warning', 'No recipient email addresses found in this batch.');
      return;
    }

    setBroadcasting(true);
    setBroadcastProgress({ completed: 0, total: Math.ceil(recipients.length / 100), totalRecipients: recipients.length });
    setBroadcastSummary(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      const htmlBody = broadcastContent.replace(/\n/g, '<br/>');
      const items = recipients.map(r => ({
        itemType: 'email_recipient',
        recipientEmail: r.email,
        applicationId: r.applicationId || null,
        inputSnapshot: {
          email: r.email,
          subject: broadcastSubject,
          htmlBody,
          applicantData: r.applicantData || null,
        },
      }));

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...workspaceHeader(),
        },
        body: JSON.stringify({
          type: 'email_campaign',
          items,
          inputSnapshot: {
            subject: broadcastSubject,
            htmlBody,
            batchId: selectedBatchId,
            senderAddress: broadcastSenderAddress || undefined,
            sentBy: adminEmail,
            attachments: broadcastAttachments.length > 0 ? broadcastAttachments : undefined,
          },
          options: {
            subject: broadcastSubject,
            htmlBody,
            fromAddr: broadcastSenderAddress || undefined,
            attachments: broadcastAttachments.length > 0 ? broadcastAttachments : undefined,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Broadcast failed');

      setBroadcastSubject('');
      setBroadcastContent('');
      setBroadcastAttachments([]);

      notifyToast('success', 'Email Campaign Queued! Tracking progress in the Task Tracker.');
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
    } catch (err) {
      notifyToast('error', 'Broadcast failed: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setBroadcasting(false);
      setBroadcastProgress(null);
    }
  };

  const handleBatchLogFileChange = (file: File | null) => {
    setBatchLogFile(file);
    if (file) {
      setBatchLogFileName(file.name);
    } else {
      setBatchLogFileName(null);
    }
    setBatchLogFileUrl(null);
  };

  const handleAddBatchLog = async () => {
    if (!selectedBatchId || (!newBatchLogText.trim() && !batchLogFile)) return;

    setIsUploadingBatchLogFile(true);
    setBatchLogFileProgress(10);

    let finalFileUrl = '';
    const fileName = batchLogFileName || '';

    if (batchLogFile) {
      try {
        let uploadPayload: Blob | File = batchLogFile;
        if (batchLogFile.type.startsWith('image/')) {
          try {
            setBatchLogFileProgress(5);
            uploadPayload = await compressImage(batchLogFile);
            setBatchLogFileProgress(15);
          } catch (compressErr) {
            console.warn("Pre-upload batch log image compression failed, using original file:", compressErr);
          }
        } else {
          setBatchLogFileProgress(35);
        }

        const storageRef = ref(storage, `batches/${selectedBatchId}/logs/${Date.now()}_${batchLogFile.name}`);
        const snapshot = await uploadBytesWithTimeout(storageRef, uploadPayload, 60000, 3, (p) => setBatchLogFileProgress(p));
        setBatchLogFileProgress(80);
        finalFileUrl = snapshot.downloadUrl;
      } catch (err) {
        console.error("Storage upload failed:", err);
        showToast(getStorageErrorMessage(err), "error");
        setIsUploadingBatchLogFile(false);
        setBatchLogFileProgress(null);
        return;
      }
    }

    setBatchLogFileProgress(95);

    const logId = 'log_' + crypto.randomUUID();
    const newLog: BatchLog = {
      logId,
      batchId: selectedBatchId,
      adminId: auth.currentUser?.uid || 'unauthenticated',
      adminName: auth.currentUser?.displayName || adminEmail.split('@')[0],
      text: newBatchLogText,
      createdAt: new Date().toISOString(),
      ...(finalFileUrl && { fileUrl: finalFileUrl, fileName })
    };

    try {
      await safeFetch(`/api/admin/batches/${selectedBatchId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newLog),
      });

      setBatchLogs([newLog, ...batchLogs]);
      setNewBatchLogText('');
      setBatchLogFile(null);
      setBatchLogFileUrl(null);
      setBatchLogFileName(null);
      setBatchLogFileProgress(null);
      await createAuditLog('ADD_BATCH_LOG', selectedBatchId, `Added administrative cohort log/comment`);
    } catch (err) {
      console.error("Failed to add batch log:", err);
      notifyToast('error', "Failed to add log entry: " + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsUploadingBatchLogFile(false);
      setBatchLogFileProgress(null);
    }
  };

  return {
    toast,
    showToast,
    batches,
    batchFolders,
    currentFolderId,
    setCurrentFolderId,
    setBatchFolders,
    fetchBatchFolders,
    handleCreateFolder,
    handleUpdateFolder,
    handleDeleteFolder,
    isCreatingBatch,
    selectedBatchId,
    batchName,
    batchDescription,
    batchProgramTypes,
    batchSearchQuery,
    batchSelectedApplicantIds,
    bulkEmailSubject,
    bulkEmailContent,
    bulkStatusValue,
    cohortSearch,
    cohortStatusFilter,
    cohortProgramFilter,
    batchLogs,
    isLoadingLogs,
    newBatchLogText,
    batchLogFile,
    isUploadingBatchLogFile,
    batchLogFileUrl,
    batchLogFileName,
    batchLogFileProgress,
    isAddApplicantOpen,
    addApplicantSearch,
    batchNotes,
    isSavingNotes,
    setBatches,
    setIsCreatingBatch,
    setSelectedBatchId,
    setBatchName,
    setBatchDescription,
    setBatchProgramTypes,
    setBatchSearchQuery,
    setBatchSelectedApplicantIds,
    setBulkEmailSubject,
    setBulkEmailContent,
    broadcastSubject,
    broadcastContent,
    broadcastAttachments,
    broadcasting,
    broadcastProgress,
    broadcastSummary,
    broadcastSenderAddress,
    setBroadcastSubject,
    setBroadcastContent,
    setBroadcastAttachments,
    setBroadcastSummary,
    setBroadcastSenderAddress,
    setBulkStatusValue,
    setCohortSearch,
    setCohortStatusFilter,
    setCohortProgramFilter,
    setBatchLogs,
    setIsLoadingLogs,
    setNewBatchLogText,
    setBatchLogFile,
    setIsUploadingBatchLogFile,
    setBatchLogFileUrl,
    setBatchLogFileName,
    setBatchLogFileProgress,
    setIsAddApplicantOpen,
    setAddApplicantSearch,
    setBatchNotes,
    setIsSavingNotes,
    fetchBatchLogs,
    fetchBatches,
    handleCreateBatch,
    handleDeleteBatch,
    handleUpdateBatchMembers,
    handleSaveBatchNotes,
    handleUpdateBatchDetail,
    handleUpdateBatchTags,
    handleBulkStatusUpdate,
    handleSendBulkEmail,
    handleBroadcastEmail,
    handleAddBatchLog,
    handleBatchLogFileChange,
    createAuditLog,
  };
}
